import React, {useCallback, useMemo, useState} from 'react';
import type {GetProjectListRequest} from 'src/api/ProactApiClient';
import {Autocomplete, type AutocompleteProps} from 'src/design/atoms/Autocomplete';
import {useGetProjectSelectOptionsQuery} from 'src/redux/queries/project';
import useDebounce from 'src/shared/hooks/useDebounce';
import type {ISelectOption} from 'src/types/Form';

export interface ProjectSelectBoxProps
  extends Omit<AutocompleteProps, 'options' | 'value'> {
  queryOptions?: Partial<GetProjectListRequest>;
  value?: string | null;
  includeInstanceWide?: boolean;
}

const INSTANCE_WIDE_OPTION: ISelectOption = {
  id: '',
  label: 'All Projects',
};

export const ProjectSelectBox: React.FC<ProjectSelectBoxProps> = React.memo(
  ({queryOptions, value, includeInstanceWide = true, ...other}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const debouncedSearchTerm = useDebounce(searchTerm, 400);

    const {data, isFetching, isLoading} = useGetProjectSelectOptionsQuery({
      ...queryOptions,
      start: 0,
      length: 50,
      search: debouncedSearchTerm || queryOptions?.search,
      sortColumn: queryOptions?.sortColumn ?? 'name',
      sortDirection: queryOptions?.sortDirection ?? 'asc',
    });

    const options = useMemo(() => {
      const projectOptions = (data?.data ?? []) as ISelectOption[];
      if (includeInstanceWide) {
        return [INSTANCE_WIDE_OPTION, ...projectOptions];
      }
      return projectOptions;
    }, [data?.data, includeInstanceWide]);

    const selectedOption = useMemo(() => {
      if (value === null || value === undefined || value === '') {
        return includeInstanceWide ? INSTANCE_WIDE_OPTION : null;
      }
      return options?.find(i => String(i.id) === String(value)) ?? null;
    }, [options, value, includeInstanceWide]);

    const handleInputChange = useCallback(
      (event: React.SyntheticEvent, newInputValue: string) => {
        setSearchTerm(newInputValue);
      },
      []
    );

    const isSearching = isFetching && debouncedSearchTerm !== '';

    return (
      <Autocomplete
        {...other}
        disabled={isLoading || other.disabled}
        options={options}
        value={selectedOption}
        onInputChange={handleInputChange}
        loading={isSearching}
      />
    );
  }
);
